/**
 * @module  public/js/services/taskCompletionService.js
 * @class   TaskCompletionService
 * @summary Completes a CRM task then logs the user-selected outcome.
 *
 * @description
 * First updates the task status in HubSpot / Salesforce via the
 * `manageUserTask` Cloud Function. Next it writes a snapshot of the outcome
 * to `/TaskOutcomes/{taskId}` so the Sequence Engine can react.
 *
 * ## Key Features
 * – CRM-aware PATCH that adds `hs_task_status` for HubSpot tasks
 * – Fire-and-forget outcome persistence with server timestamp
 * – Pre- and post-analytics hooks instrumentation
 * – Graceful fallback when auth is not yet ready (skips PATCH)
 *
 * ## Public API
 * – `complete(taskId)` — legacy helper, marks task *COMPLETED* only
 * – `completeWithOutcome(dto)` — preferred; status + outcome snapshot
 *
 * ## Dependencies
 * { analyticsService, apiClient, auth, crmUtils, endpoints, tenantResolver }
 *
 * @author   Auto-Generated
 * @version  1.1.3
 * @since    2025-05-05
 */
import {
  getFunctions,
  httpsCallable,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-functions.js';
import { firebaseApp } from '../core/firebase-service.js';
import { CtaInstance } from '../focusmode/cta-instance.js';
import TenantResolver from './tenantResolver.js';

const functions = getFunctions(firebaseApp, 'us-central1');

export default class TaskCompletionService {
  #apiClient;
  #endpoints;
  #crmUtils;
  #analyticsService;
  #auth;
  #tenantResolver;
  #recordTaskOutcome;
  #warmUpPromise = null;

  /**
   * @param {object} deps
   * @param {import('../core/apiclient.js').default}             deps.apiClient
   * @param {import('../core/endpoints-service.js').default}     deps.endpoints
   * @param {{ getCurrentCRM(uid:string):Promise<string> }}      deps.crmUtils
   * @param {import('./analyticsservice.js').default}            deps.analyticsService
   * @param {import('firebase/auth').Auth}                       deps.auth
   * @param {import('./tenantResolver.js').default=}             deps.tenantResolver
   */
  constructor({
    apiClient,
    endpoints,
    crmUtils,
    analyticsService,
    auth,
    tenantResolver,
  }) {
    this.#apiClient = apiClient;
    this.#endpoints = endpoints;
    this.#crmUtils = crmUtils;
    this.#analyticsService = analyticsService;
    this.#auth = auth;
    this.#tenantResolver = tenantResolver || new TenantResolver(auth);
    this.#recordTaskOutcome = httpsCallable(functions, 'recordTaskOutcome');
  }

  /**
   * Warm the recordTaskOutcome callable without completing or mutating a task.
   *
   * @param {{reason?: string}} [options={}] Warmup metadata for diagnostics.
   * @returns {Promise<Record<string, unknown>>} Callable warmup response data.
   */
  async warmUp(options = {}) {
    if (this.#warmUpPromise) return this.#warmUpPromise;
    const reason =
      typeof options.reason === 'string' && options.reason.trim()
        ? options.reason.trim()
        : 'focusmode-start';

    this.#warmUpPromise = (async () => {
      const user =
        (await this.#auth.waitForLogin?.(5_000).catch(() => null)) ??
        this.#auth.currentUser;
      if (!user) {
        throw new Error('TaskCompletionService: auth not ready - retry later.');
      }
      const tenantId =
        user.tenantId ?? (await this.#tenantResolver.resolveTenantId());
      await user.getIdToken?.(!tenantId);
      const res = await this.#recordTaskOutcome({
        action: 'warmup',
        tenantId,
        reason,
      });
      return res?.data || { status: 'warmed' };
    })().catch((err) => {
      this.#warmUpPromise = null;
      throw err;
    });

    return this.#warmUpPromise;
  }

  /*───────────────── Legacy method (status-only) ─────────────────*/

  /**
   * Mark the task as **COMPLETED** (no outcome document).
   * @param {string} taskId
   */
  async complete(taskId) {
    await this.#patchStatus(null, taskId, 'COMPLETED');
  }

  /*────────────── Preferred method: status + outcome ──────────────*/

  /**
   * Complete the task **and** persist its outcome snapshot.
   * Forwards optional `behaviors` and `metadata` fields to the backend.
   * @param {import('./outcomeService.js').OutcomeDTO & { endSequence?: boolean }} dto
   * @param {{ tenantIdOverride?: string, ctaInstanceId?: string }} [opts] Explicit tenant ID and CTA instance ID override
   */
  async completeWithOutcome(
    taskOrDto,
    maybeDto,
    { tenantIdOverride, ctaInstanceId } = {}
  ) {
    let task = null;
    let outcomeDto = null;
    if (taskOrDto && typeof taskOrDto === 'object' && taskOrDto.id) {
      task = taskOrDto;
      outcomeDto = maybeDto || {};
    } else {
      outcomeDto = taskOrDto || {};
    }

    const user =
      (await this.#auth.waitForLogin(5_000).catch(() => null)) ??
      this.#auth.currentUser;

    if (!user) {
      throw new Error('TaskCompletionService: auth not ready – retry later.');
    }

    if (!outcomeDto || typeof outcomeDto !== 'object') {
      console.error('[TaskCompletionService] missing outcomeDto', {
        task,
        outcomeDto,
        tenantIdOverride,
      });
      throw new Error('outcomeDto is required');
    }

    outcomeDto.userId ??= user.uid;
    outcomeDto.counts_toward_goal ??= true;
    if (outcomeDto.metadata && typeof outcomeDto.metadata === 'object') {
      Object.keys(outcomeDto.metadata).forEach(
        (k) =>
          outcomeDto.metadata[k] === undefined && delete outcomeDto.metadata[k]
      );
    }
    if (task) {
      outcomeDto.sequenceId ??= task.sequenceId ?? task.enrolId;
      outcomeDto.stepIndex ??= task.stepIdx;
    }

    const taskId = task?.id || task?.taskId || outcomeDto.taskId;
    let ctaId = ctaInstanceId ?? (taskId ? CtaInstance.get(taskId) : null);
    if (!ctaId && taskId) {
      ctaId = CtaInstance.ensure(taskId);
    }
    const crmTaskId =
      task?.crm?.externalTaskId ||
      task?.crmTaskId ||
      task?.metadata?.crmTaskId ||
      outcomeDto.crmTaskId ||
      outcomeDto.metadata?.crmTaskId ||
      null;
    const tenantId =
      tenantIdOverride ??
      user.tenantId ??
      (await this.#tenantResolver.resolveTenantId());

    // Warm the cached token without forcing a refresh on every Focus Mode outcome.
    // Force only when tenant context is still missing and claims may need repair.
    await user.getIdToken(!tenantId);

    const contactId =
      outcomeDto.contactId ??
      task?.contactId ??
      task?.associated_contact_id ??
      task?.contact_id ??
      task?.associations?.contactId ??
      null;
    const accountId =
      outcomeDto.accountId ??
      task?.accountId ??
      task?.associatedAccountId ??
      null;
    const sequenceId =
      outcomeDto.sequenceId ?? task?.sequenceId ?? task?.enrolId ?? null;
    const stepIndex =
      outcomeDto.stepIndex ?? task?.stepIndex ?? task?.stepIdx ?? null;

    try {
      if (!tenantId || !taskId || !ctaId) {
        console.warn('[TaskCompletionService] missing identifiers', {
          tenantId,
          taskId,
          ctaInstanceId: ctaId,
        });
        return Promise.reject(
          new Error(
            'TaskCompletionService: tenantId, taskId, and ctaInstanceId are required'
          )
        );
      }

      let action = outcomeDto.action;
      let outcomeType = outcomeDto.outcome;
      if (
        outcomeDto.channel === 'LINKEDIN' ||
        outcomeDto.channel === 'LINKEDIN_ACTIVITY'
      ) {
        outcomeType = 'SOCIAL';
        action = action || 'VIEWED_PROFILE';
      } else {
        action = action || outcomeDto.outcome;
      }

      const payload = {
        tenantId,
        taskId,
        ctaInstanceId: ctaId,
        action,
        outcomeType,
        channel: outcomeDto.channel,
        contactId,
        accountId,
        sequenceId,
        stepIdx: stepIndex,
        stepIndex,
        behaviors: outcomeDto.behaviors ?? null,
        metadata: outcomeDto.metadata ?? null,
      };
      if (outcomeDto.endSequence) {
        payload.endSequence = outcomeDto.endSequence;
      }
      await this.#recordTaskOutcome(payload);
    } catch (err) {
      console.error('[TaskCompletionService] recordTaskOutcome aborted', {
        tenantId,
        taskId,
        ctaInstanceId: ctaId,
        message: err.message,
      });
      throw err;
    }

    try {
      let crmIdForPatch = null;
      let crmIdSource = 'none';
      if (crmTaskId) {
        crmIdForPatch = crmTaskId;
        crmIdSource = 'crm-task-id';
      } else if (task && task.crm && task.crm.externalTaskId) {
        crmIdForPatch = task.crm.externalTaskId;
        crmIdSource = 'enriched-dto';
      } else if (outcomeDto.taskId) {
        crmIdForPatch = outcomeDto.taskId;
        crmIdSource = 'dto-task-id';
      }
      const isMirrorId =
        typeof crmIdForPatch === 'string' && crmIdForPatch.startsWith('mt_');

      if (crmIdForPatch && !isMirrorId) {
        await this.#patchStatus(user, crmIdForPatch, 'COMPLETED');
        console.info({
          event: 'task_complete',
          mode: 'crm+mirror',
          taskId: crmIdForPatch,
          source: crmIdSource,
        });
      } else {
        const reason = crmIdForPatch ? 'mirror-task-id' : 'no-crm-id';
        if (crmIdForPatch) {
          console.warn(
            '[TaskCompletionService] skipping CRM patch for mirror ID',
            {
              taskId: crmIdForPatch,
            }
          );
        }
        console.info({
          event: 'task_complete',
          mode: 'mirror-only',
          reason,
          taskId,
        });
      }
    } catch (err) {
      console.error('[TaskCompletionService] CRM PATCH failed', {
        message: err?.message,
        code: err?.code,
      });
    }

    return 'ok';
  }

  /*──────────────────── Internal helpers ────────────────────*/

  /**
   * PATCH task status via backend endpoint.
   * @param {import('firebase/auth').User|null} userFromCaller
   * @param {string}  taskId
   * @param {'COMPLETED'|'CANCELLED'|'PENDING'} status
   * @private
   */
  async #patchStatus(userFromCaller, taskId, status) {
    const user =
      userFromCaller ??
      (await this.#auth.waitForLogin?.(5_000).catch(() => null)) ??
      this.#auth.currentUser;

    if (!user) {
      throw new Error('TaskCompletionService: auth not ready – retry later.');
    }

    /* pre-analytics */
    this.#analyticsService.track('FocusMode - TaskCompleted', { taskId });

    const crm = await this.#crmUtils.getCurrentCRM(user.uid);
    const url = this.#endpoints.manageUserTask();

    const body =
      crm === 'hubspot'
        ? {
            userId: user.uid,
            tenantId: user.tenantId,
            crm, // "hubspot"
            action: 'update',
            taskId,
            status, // HubSpot handler needs top-level
          }
        : {
            /* Salesforce (or any other CRM that still expects patch) */
            userId: user.uid,
            tenantId: user.tenantId,
            crm, // ← now dynamic
            action: 'update',
            taskId,
            status, // SF handler reads this
            patch: { status }, // legacy key (harmless; remove if unneeded)
          };

    await this.#apiClient.fetchJson(url, { auth: true, body });
    /* post-analytics */
    this.#analyticsService.track('FocusMode - TaskCompleted', {
      taskId,
      post: true,
    });
  }
}

/* Helper re-export for functional style (back-compat + new signature)
 * Supports:
 *   completeWithOutcome(svc, dto)
 *   completeWithOutcome(svc, task, dto)
 */
export const completeWithOutcome = (svc, a, b, c) => {
  return c !== undefined
    ? svc.completeWithOutcome(a, b, c)
    : b === undefined
      ? svc.completeWithOutcome(a)
      : svc.completeWithOutcome(a, b);
};
